/* ==========================================================================
   Lumen — scripts del tema
   Sin dependencias. Usa la API AJAX del carrito y la Section Rendering API
   de Shopify para mantener los datos reales (precios, inventario, variantes).
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, settings: {}, strings: {} };
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const parser = new DOMParser();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => parser.parseFromString(html, 'text/html');

  const sectionInner = (html, selector) => {
    const element = parseHTML(html).querySelector(selector);
    return element ? element.innerHTML : null;
  };

  /* ---------- Gestión del foco ---------- */
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
  let trapHandler = null;

  const focusableIn = (container) =>
    $$(FOCUSABLE, container).filter((el) => el.getClientRects().length > 0);

  function trapFocus(container, elementToFocus) {
    removeTrapFocus();
    trapHandler = (event) => {
      if (event.key !== 'Tab') return;
      const elements = focusableIn(container);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler);
    const target = elementToFocus || focusableIn(container)[0] || container;
    target.focus({ preventScroll: true });
  }

  function removeTrapFocus(elementToFocus) {
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
    if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus({ preventScroll: true });
  }

  /* ---------- Animación de aparición ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -40px 0px', threshold: 0.05 }
        )
      : null;

  function initReveal(root = document) {
    const items = $$('.reveal:not(.is-visible)', root);
    if (!revealObserver || !theme.settings.animations || reducedMotion || (window.Shopify && window.Shopify.designMode)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    items.forEach((item) => revealObserver.observe(item));
  }

  /* ---------- Cajones genéricos ---------- */
  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    get panel() {
      return $('[data-drawer-panel]', this) || this;
    }

    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      if (this.trigger && this.trigger.hasAttribute('aria-expanded')) this.trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => trapFocus(this.panel, $('[data-autofocus]', this)));
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      if (this.trigger && this.trigger.hasAttribute('aria-expanded')) this.trigger.setAttribute('aria-expanded', 'false');
      removeTrapFocus(this.trigger);
    }
  }

  customElements.define('theme-drawer', ThemeDrawer);
  customElements.define('cart-drawer', class extends ThemeDrawer {});
  customElements.define('menu-drawer', class extends ThemeDrawer {});
  customElements.define(
    'search-modal',
    class extends ThemeDrawer {
      open(trigger) {
        super.open(trigger);
        const input = $('input[type="search"]', this);
        if (input) setTimeout(() => input.focus(), 50);
      }
    }
  );

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-open-drawer]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.openDrawer);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    $$('.drawer.is-open').forEach((openDrawer) => {
      if (openDrawer !== drawer) openDrawer.close();
    });
    drawer.open(trigger);
  });

  /* ---------- Encabezado ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (header) {
      const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    // Menús desplegables: cerrar al hacer clic fuera, con Escape o al abrir otro.
    document.addEventListener('click', (event) => {
      $$('details.nav-dropdown[open]').forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      $$('details.nav-dropdown[open]').forEach((details) => {
        details.removeAttribute('open');
        const summary = $('summary', details);
        if (summary) summary.focus();
      });
    });

    $$('details.nav-dropdown').forEach((details) => {
      details.addEventListener('toggle', () => {
        const summary = $('summary', details);
        if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        if (!details.open) return;
        $$('details.nav-dropdown[open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      });
    });
  }

  /* ---------- Barra de anuncios ---------- */
  customElements.define(
    'announcement-bar',
    class extends HTMLElement {
      connectedCallback() {
        this.slides = $$('[data-slide]', this);
        if (this.slides.length < 2) return;
        this.index = 0;
        $('[data-prev]', this)?.addEventListener('click', () => this.go(this.index - 1));
        $('[data-next]', this)?.addEventListener('click', () => this.go(this.index + 1));
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
        this.start();
      }

      disconnectedCallback() {
        this.stop();
      }

      go(index) {
        this.index = (index + this.slides.length) % this.slides.length;
        this.slides.forEach((slide, i) => {
          slide.hidden = i !== this.index;
        });
      }

      start() {
        if (reducedMotion || Number(this.dataset.speed) === 0) return;
        this.stop();
        this.timer = setInterval(() => this.go(this.index + 1), (Number(this.dataset.speed) || 5) * 1000);
      }

      stop() {
        clearInterval(this.timer);
      }
    }
  );

  /* ---------- Carrito ---------- */
  const Cart = {
    sectionIds() {
      const ids = ['cart-icon-bubble'];
      if ($('cart-drawer')) ids.push('cart-drawer');
      const main = $('[data-main-cart]');
      if (main) ids.push(main.dataset.sectionId);
      return ids;
    },

    render(sections) {
      if (!sections) return;

      if (sections['cart-icon-bubble']) {
        const html = sectionInner(sections['cart-icon-bubble'], '.shopify-section');
        if (html !== null) $$('[data-cart-count]').forEach((el) => (el.innerHTML = html));
      }

      const drawerTarget = $('cart-drawer [data-cart-drawer-inner]');
      if (sections['cart-drawer'] && drawerTarget) {
        const html = sectionInner(sections['cart-drawer'], '[data-cart-drawer-inner]');
        if (html !== null) drawerTarget.innerHTML = html;
      }

      const main = $('[data-main-cart]');
      if (main && sections[main.dataset.sectionId]) {
        const html = sectionInner(sections[main.dataset.sectionId], '[data-main-cart-inner]');
        const target = $('[data-main-cart-inner]', main);
        if (html !== null && target) target.innerHTML = html;
      }

      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    async change(line, quantity) {
      const response = await fetch(`${theme.routes.cartChange}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line,
          quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }
      return data;
    },

    async updateNote(note) {
      await fetch(`${theme.routes.cartUpdate}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
    }
  };

  customElements.define(
    'cart-items',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
      }

      onChange(event) {
        const input = event.target;
        if (input.matches('[data-cart-note]')) {
          Cart.updateNote(input.value);
          return;
        }
        if (!input.matches('[data-line-quantity]')) return;
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.update(Number(input.dataset.line), quantity, input);
      }

      onClick(event) {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        this.update(Number(remove.dataset.line), 0, remove);
      }

      async update(line, quantity, source) {
        const item = source.closest('[data-cart-item]');
        const errorEl = item ? $('[data-item-error]', item) : null;
        this.classList.add('is-loading');
        this.setAttribute('aria-busy', 'true');
        try {
          const data = await Cart.change(line, quantity);
          const updated = data.items[line - 1];
          const key = item ? item.dataset.key : null;
          const finalItem = key ? data.items.find((i) => i.key === key) : updated;
          Cart.render(data.sections);
          if (quantity > 0 && finalItem && finalItem.quantity < quantity) {
            const newItem = $(`[data-cart-item][data-key="${CSS.escape(finalItem.key)}"] [data-item-error]`);
            if (newItem) {
              newItem.textContent = theme.strings.quantityError.replace('__QTY__', finalItem.quantity);
              newItem.hidden = false;
            }
          }
        } catch (error) {
          if (errorEl) {
            errorEl.textContent = error.message || theme.strings.cartError;
            errorEl.hidden = false;
          }
        } finally {
          this.classList.remove('is-loading');
          this.removeAttribute('aria-busy');
        }
      }
    }
  );

  /* ---------- Selector de cantidad ---------- */
  customElements.define(
    'quantity-input',
    class extends HTMLElement {
      connectedCallback() {
        this.input = $('input', this);
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button[name]');
          if (!button) return;
          event.preventDefault();
          const previous = this.input.value;
          if (button.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }
  );

  /* ---------- Formulario de producto (añadir al carrito) ---------- */
  customElements.define(
    'product-form',
    class extends HTMLElement {
      connectedCallback() {
        this.form = $('form', this);
        if (!this.form) return;
        this.submitButton = $('[type="submit"]', this.form);
        this.errorEl = $('[data-form-error]', this);
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      async onSubmit(event) {
        // Con carrito de página dejamos el envío nativo (redirige a /cart).
        if (theme.settings.cartType !== 'drawer' || !$('cart-drawer')) return;
        event.preventDefault();
        if (!this.submitButton || this.submitButton.disabled || this.submitButton.classList.contains('is-loading')) return;

        this.showError(null);
        this.submitButton.classList.add('is-loading');
        this.submitButton.setAttribute('aria-busy', 'true');

        const formData = new FormData(this.form);
        formData.append('sections', Cart.sectionIds().join(','));
        formData.append('sections_url', window.location.pathname);

        try {
          const response = await fetch(`${theme.routes.cartAdd}`, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
            body: formData
          });
          const data = await response.json();
          if (data.status) {
            this.showError(data.description || data.message);
            return;
          }
          Cart.render(data.sections);
          $('cart-drawer').open(this.submitButton);
        } catch (error) {
          this.showError(theme.strings.cartError);
        } finally {
          this.submitButton.classList.remove('is-loading');
          this.submitButton.removeAttribute('aria-busy');
        }
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }
    }
  );

  /* ---------- Selector de variantes ---------- */
  customElements.define(
    'variant-picker',
    class extends HTMLElement {
      connectedCallback() {
        const json = $('[data-variants]', this);
        this.variants = json ? JSON.parse(json.textContent) : [];
        this.section = this.closest('[data-product-section]') || document;
        this.addEventListener('change', () => this.onChange());
        this.updateAvailability();
      }

      get groups() {
        return $$('[data-option-group]', this);
      }

      selectedOptions() {
        return this.groups.map((group) => {
          if (group.tagName === 'SELECT') return group.value;
          const checked = $('input:checked', group);
          return checked ? checked.value : null;
        });
      }

      onChange() {
        const selected = this.selectedOptions();
        this.current = this.variants.find((variant) => variant.options.every((value, i) => value === selected[i]));
        this.updateAvailability();
        this.updateLegends(selected);
        this.updateForm();
        if (!this.current) return;
        this.updateURL();
        this.updatePrice();
        this.updateDetails();
        const gallery = $('media-gallery', this.section);
        if (gallery && this.current.media) gallery.setActive(String(this.current.media));
      }

      updateAvailability() {
        const selected = this.selectedOptions();
        this.groups.forEach((group, position) => {
          const inputs = group.tagName === 'SELECT' ? $$('option', group) : $$('input', group);
          inputs.forEach((input) => {
            const available = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options[position] === input.value &&
                variant.options.slice(0, position).every((value, i) => value === selected[i])
            );
            input.classList.toggle('is-unavailable', !available);
            if (input.tagName === 'OPTION') {
              const label = input.dataset.label || input.value;
              input.textContent = available ? label : `${label} — ${theme.strings.unavailable}`;
            }
          });
        });
      }

      updateLegends(selected) {
        this.groups.forEach((group, i) => {
          const label = $('[data-selected-value]', group.closest('[data-option]') || group);
          if (label && selected[i]) label.textContent = selected[i];
        });
      }

      updateForm() {
        const formId = this.dataset.formId;
        const form = formId ? document.getElementById(formId) : null;
        if (!form) return;
        const idInput = $('input[name="id"]', form);
        const button = $('[data-add-button]', form);
        const label = button ? $('[data-add-label]', button) : null;

        if (!this.current) {
          if (button) button.disabled = true;
          if (label) label.textContent = theme.strings.unavailable;
          return;
        }
        if (idInput) {
          idInput.value = this.current.id;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // Un precio sin definir (0) nunca se puede comprar.
        const canBuy = this.current.available && !this.current.pricePending;
        if (button) button.disabled = !canBuy;
        if (label) {
          if (this.current.pricePending) label.textContent = theme.strings.priceComingSoon;
          else label.textContent = this.current.available ? theme.strings.addToCart : theme.strings.soldOut;
        }
      }

      updateURL() {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.current.id}`);
      }

      updatePrice() {
        const container = $('[data-price-container]', this.section);
        if (!container) return;
        const variant = this.current;
        const compare = variant.compare
          ? `<span class="visually-hidden">${container.dataset.labelRegular || ''}</span><s class="price__compare">${variant.compare}</s>`
          : '';
        const badge = variant.compare && variant.save ? `<span class="price__badge">${variant.save}</span>` : '';
        container.innerHTML = `<div class="price price--large${variant.pricePending ? ' price--pending' : ''}${variant.compare ? ' price--on-sale' : ''}">
            <span class="price__current">${variant.price}</span>${compare}${badge}
          </div>`;
      }

      updateDetails() {
        const variant = this.current;
        const sku = $('[data-sku]', this.section);
        if (sku) {
          sku.textContent = variant.sku || '';
          const wrapper = sku.closest('[data-sku-wrapper]');
          if (wrapper) wrapper.hidden = !variant.sku;
        }
        const inventory = $('[data-inventory]', this.section);
        if (inventory) {
          const threshold = Number(inventory.dataset.threshold || 0);
          let state = 'in';
          let text = theme.strings.inStock;
          if (!variant.available) {
            state = 'out';
            text = theme.strings.outOfStock;
          } else if (variant.inventory !== null && variant.inventory > 0 && variant.inventory <= threshold) {
            state = 'low';
            text = theme.strings.lowStock.replace('__QTY__', variant.inventory);
          }
          inventory.dataset.state = state;
          inventory.textContent = text;
        }
      }
    }
  );

  /* ---------- Galería de producto ---------- */
  customElements.define(
    'media-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.viewer = $('[data-gallery-viewer]', this);
        if (!this.viewer) return;
        this.slides = $$('[data-slide]', this);
        this.thumbs = $$('[data-thumb]', this);

        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.setActive(thumb.dataset.mediaId))
        );
        $('[data-gallery-prev]', this)?.addEventListener('click', () => this.step(-1));
        $('[data-gallery-next]', this)?.addEventListener('click', () => this.step(1));
        this.viewer.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.step(1);
          if (event.key === 'ArrowLeft') this.step(-1);
        });

        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.markActive(entry.target.dataset.mediaId);
              });
            },
            { root: this.viewer, threshold: 0.6 }
          );
          this.slides.forEach((slide) => observer.observe(slide));
        }

        const initial = this.dataset.initial;
        if (initial) this.setActive(initial, true);
      }

      currentIndex() {
        const active = this.slides.findIndex((slide) => slide.dataset.mediaId === this.activeId);
        return active < 0 ? 0 : active;
      }

      step(direction) {
        if (!this.slides.length) return;
        const index = (this.currentIndex() + direction + this.slides.length) % this.slides.length;
        this.setActive(this.slides[index].dataset.mediaId);
      }

      setActive(mediaId, instant = false) {
        const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
        if (!slide) return;
        this.viewer.scrollTo({ left: slide.offsetLeft, behavior: instant || reducedMotion ? 'auto' : 'smooth' });
        this.markActive(slide.dataset.mediaId);
        // Pausar videos que no estén visibles.
        this.slides.forEach((s) => {
          if (s === slide) return;
          $$('video', s).forEach((video) => video.pause());
        });
      }

      markActive(mediaId) {
        this.activeId = String(mediaId);
        this.thumbs.forEach((thumb) => {
          const active = thumb.dataset.mediaId === this.activeId;
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
          if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      }
    }
  );

  /* ---------- Compartir ---------- */
  customElements.define(
    'share-button',
    class extends HTMLElement {
      connectedCallback() {
        const button = $('button', this);
        if (!button) return;
        button.addEventListener('click', async () => {
          const url = this.dataset.url;
          if (navigator.share) {
            try {
              await navigator.share({ title: this.dataset.title, url });
            } catch (e) {
              /* cancelado por el usuario */
            }
            return;
          }
          try {
            await navigator.clipboard.writeText(url);
            const label = $('[data-share-label]', this);
            if (label) {
              const original = label.textContent;
              label.textContent = theme.strings.linkCopied;
              setTimeout(() => (label.textContent = original), 2000);
            }
          } catch (e) {
            window.prompt('', url);
          }
        });
      }
    }
  );

  /* ---------- Búsqueda predictiva ---------- */
  customElements.define(
    'predictive-search',
    class extends HTMLElement {
      connectedCallback() {
        this.input = $('input[type="search"]', this);
        this.results = $('[data-predictive-results]', this);
        if (!this.input || !this.results || !theme.settings.predictiveSearch) return;
        this.cache = new Map();
        this.input.addEventListener('input', debounce(() => this.onInput(), 220));
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            const first = $('a', this.results);
            if (first) {
              event.preventDefault();
              first.focus();
            }
          }
        });
        this.results.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          const links = $$('a', this.results);
          const index = links.indexOf(document.activeElement);
          if (index < 0) return;
          event.preventDefault();
          if (event.key === 'ArrowUp' && index === 0) {
            this.input.focus();
            return;
          }
          const next = links[index + (event.key === 'ArrowDown' ? 1 : -1)];
          if (next) next.focus();
        });
      }

      async onInput() {
        const term = this.input.value.trim();
        if (!term) {
          this.close();
          return;
        }
        if (this.cache.has(term)) {
          this.show(this.cache.get(term));
          return;
        }
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();

        const params = new URLSearchParams({
          q: term,
          section_id: 'predictive-search',
          'resources[type]': 'product,collection,page,query',
          'resources[limit]': '6',
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag'
        });

        try {
          const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, {
            signal: this.controller.signal
          });
          if (!response.ok) throw new Error(String(response.status));
          const html = sectionInner(await response.text(), '#shopify-section-predictive-search');
          if (html === null) throw new Error('empty');
          this.cache.set(term, html);
          if (this.input.value.trim() === term) this.show(html);
        } catch (error) {
          if (error.name !== 'AbortError') this.close();
        }
      }

      show(html) {
        this.results.innerHTML = html;
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.results.hidden = true;
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
      }
    }
  );

  /* ---------- Filtros y ordenamiento ---------- */
  customElements.define(
    'facet-filters',
    class extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.panel = $('[data-facets-panel]', this);
        this.debouncedSubmit = debounce(() => this.submit(), 450);

        this.addEventListener('change', (event) => {
          if (!event.target.closest('#FacetForm, [form="FacetForm"]')) return;
          this.debouncedSubmit();
        });
        this.addEventListener('input', (event) => {
          if (event.target.matches('[data-price-input]')) this.debouncedSubmit();
        });
        this.addEventListener('submit', (event) => {
          if (event.target.id !== 'FacetForm') return;
          event.preventDefault();
          this.submit();
        });
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-facet-link]');
          if (link) {
            event.preventDefault();
            this.render(link.getAttribute('href'));
            return;
          }
          if (event.target.closest('[data-facets-open]')) {
            event.preventDefault();
            this.openPanel(event.target.closest('[data-facets-open]'));
          }
          if (event.target.closest('[data-facets-close]')) {
            event.preventDefault();
            this.closePanel();
          }
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.panel && this.panel.classList.contains('is-open')) this.closePanel();
        });

        window.addEventListener('popstate', () => this.render(window.location.href, false));
      }

      openPanel(trigger) {
        if (!this.panel) return;
        this.trigger = trigger;
        this.panel.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        trigger.setAttribute('aria-expanded', 'true');
        trapFocus($('.facets-panel__inner', this.panel) || this.panel);
      }

      closePanel() {
        if (!this.panel) return;
        this.panel.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
        removeTrapFocus(this.trigger);
      }

      submit() {
        const form = document.getElementById('FacetForm');
        if (!form) return;
        const params = new URLSearchParams();
        new FormData(form).forEach((value, key) => {
          if (value !== '') params.append(key, value);
        });
        this.render(`${form.getAttribute('action') || window.location.pathname}?${params.toString()}`);
      }

      async render(url, push = true) {
        const target = new URL(url, window.location.origin);
        const fetchUrl = new URL(target);
        fetchUrl.searchParams.set('section_id', this.sectionId);

        const results = $('[data-facets-results]', this);
        if (results) results.classList.add('is-loading');

        try {
          const response = await fetch(fetchUrl.toString());
          const html = await response.text();
          const doc = parseHTML(html);

          const openIds = $$('details.facet[open]', this).map((d) => d.id);
          const activeId = document.activeElement ? document.activeElement.id : null;

          ['[data-facets-results]', '[data-facets-inner]', '[data-product-count]', '[data-active-facets]'].forEach(
            (selector) => {
              const source = $(selector, doc);
              const destination = $(selector, this);
              if (source && destination) destination.innerHTML = source.innerHTML;
            }
          );

          $$('details.facet', this).forEach((details) => {
            details.open = openIds.includes(details.id) || details.hasAttribute('data-force-open');
          });
          if (activeId) {
            const el = document.getElementById(activeId);
            if (el && this.contains(el)) el.focus({ preventScroll: true });
          }

          const sort = $('[data-sort-select]', this);
          const sortValue = target.searchParams.get('sort_by');
          if (sort && sortValue) sort.value = sortValue;

          if (push) window.history.pushState({}, '', `${target.pathname}${target.search}`);
          initReveal(this);
        } catch (error) {
          window.location.href = target.toString();
        } finally {
          const freshResults = $('[data-facets-results]', this);
          if (freshResults) freshResults.classList.remove('is-loading');
        }
      }
    }
  );

  /* ---------- Productos recomendados ---------- */
  customElements.define(
    'product-recommendations',
    class extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.url) return;
        const load = () => {
          fetch(this.dataset.url)
            .then((response) => response.text())
            .then((text) => {
              const fresh = parseHTML(text).querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim().length) {
                this.innerHTML = fresh.innerHTML;
                initReveal(this);
              } else {
                const section = this.closest('.shopify-section');
                if (section) section.hidden = true;
              }
            })
            .catch(() => {});
        };
        if (!('IntersectionObserver' in window)) {
          load();
          return;
        }
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        observer.observe(this);
      }
    }
  );

  /* ---------- Inicio ---------- */
  initHeader();
  initReveal();

  // Editor de temas: reinicializar al recargar secciones.
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = $('cart-drawer', event.target);
    if (drawer) drawer.open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = $('cart-drawer', event.target);
    if (drawer) drawer.close();
  });
})();
